import { useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { useHistory } from "react-router-dom";
import {
  Badge,
  Button,
  ButtonVariant,
  Spinner,
  Text,
  TextField,
  Toggle,
} from "@merge-api/merge-javascript-shared";
import { Check, ChevronDown, ChevronRight, ExternalLink, Pencil, Plus, Trash2 } from "lucide-react";
import { fetchWithAuth } from "../../../api-client/api_client";
import { showErrorToast, showSuccessToast } from "../../shared/Toasts";
import { navigateToBlueprintEditor } from "../../../router/RouterUtils";
import { Plan, PlanStep, PlanSummary } from "../types";
import { displayPlanMode, displayStepType, fullPlanMode, summarizeEligibility } from "../helpers";
import { ACCENT, ACCENT_BG_TINT, ACCENT_BORDER_TINT } from "../constants";
import { conductorDelete, conductorPost } from "../conductorApi";
import { useConductorResource } from "../hooks/useConductorResource";
import ConductorCard from "./ConductorCard";
import { EnabledBadge, KindBadge, ModeBadge } from "./ConductorBadges";
import StepEditorDrawer from "./StepEditorDrawer";
import { Dialog } from "@merge-api/merge-javascript-shared/dist/designSystem/molecules/Dialog";

interface Props {
  planId: string;
  onOpenPlan: (planId: string) => void;
  onDeleted: () => void;
}

// Minimal shape of the integration blueprints endpoint we need to resolve a
// step's blueprint_id to a name + the version id used for navigation.
interface BlueprintMeta {
  id: string;
  blueprint_id: string;
  name: string;
  human_name: string;
}

interface BlueprintRef {
  name: string;
  versionId: string;
}

type BlueprintsById = Record<string, BlueprintRef>;

interface BlueprintOption {
  id: string;
  name: string;
}

interface ResolvedStep {
  isNestedPlan: boolean;
  targetId: string | null;
  targetName: string | null;
  blueprintVersionId: string | null;
  syncTypes: string[];
}

// A plan step in the editable working copy. localId keys the React list and
// identifies a step before it has a server id (newly added).
interface DraftStep extends PlanStep {
  localId: string;
}

// Editable plan-level settings + steps, cached locally until the user saves.
interface PlanSettingsDraft {
  mode: string;
  maxConcurrentSteps: number | null;
  strictPriority: boolean;
  sequentialSuccess: boolean;
  enabled: boolean;
}

interface PlanDraft {
  settings: PlanSettingsDraft;
  steps: DraftStep[];
}

const settingsFromPlan = (plan: Plan): PlanSettingsDraft => ({
  mode: plan.mode || fullPlanMode("RUN_ALL"),
  maxConcurrentSteps: plan.max_concurrent_steps,
  strictPriority: plan.enforce_strict_priority_ordering,
  sequentialSuccess: plan.require_sequential_success,
  enabled: plan.enabled,
});

const draftFromPlan = (plan: Plan): PlanDraft => ({
  settings: settingsFromPlan(plan),
  steps: plan.steps.map((step, index) => ({ ...step, localId: step.id ?? `seed-${index}` })),
});

// Comparable form of a step, used to detect unsaved changes and to skip
// re-writing untouched steps.
const canonicalStep = (step: PlanStep): string =>
  JSON.stringify({
    id: step.id,
    type: step.type,
    blueprint_id: step.blueprint_id,
    nested_plan_id: step.nested_plan_id,
    eligibility: step.eligibility,
    priority: step.priority,
    enabled: step.enabled,
    metadata: step.metadata,
  });

const resolveStep = (
  step: PlanStep,
  blueprintsById: BlueprintsById,
  planNamesById: Record<string, string>
): ResolvedStep => {
  const isNestedPlan = displayStepType(step.type) === "PLAN";
  const syncTypes = [...new Set(step.eligibility.flatMap((rule) => rule.sync_types))];
  if (isNestedPlan) {
    const nestedName =
      step.nested_plan?.name ??
      (step.nested_plan_id ? planNamesById[step.nested_plan_id] ?? null : null);
    return {
      isNestedPlan,
      targetId: step.nested_plan_id,
      targetName: nestedName,
      blueprintVersionId: null,
      syncTypes,
    };
  }
  const bp = step.blueprint_id ? blueprintsById[step.blueprint_id] : undefined;
  return {
    isNestedPlan,
    targetId: step.blueprint_id,
    targetName: bp?.name ?? null,
    blueprintVersionId: bp?.versionId ?? null,
    syncTypes,
  };
};

const MODES = [
  {
    value: "RUN_ALL",
    description: "Runs every eligible step in parallel.",
  },
  {
    value: "RUN_ONE",
    description: "Runs only the highest-priority matching step, like a router.",
  },
] as const;

// A settings row: label + explanation on the left, control on the right.
const GateRow = ({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: ReactNode;
}) => (
  <div className="flex items-center justify-between gap-6 border-b border-gray-10 py-4 last:border-b-0">
    <div className="min-w-0">
      <Text variant="md" className="text-gray-90">
        {label}
      </Text>
      {description && (
        <Text variant="sm" className="mt-0.5 block text-gray-50">
          {description}
        </Text>
      )}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

const SyncTypeChip = ({ children }: { children: ReactNode }) => (
  <span className="rounded bg-gray-10 px-1.5 py-0.5 font-mono text-xs text-gray-70">
    {children}
  </span>
);

const SyncTypeCell = ({ syncTypes }: { syncTypes: string[] }) =>
  syncTypes.length ? (
    <div className="flex flex-wrap gap-1">
      {syncTypes.map((syncType) => (
        <SyncTypeChip key={syncType}>{syncType}</SyncTypeChip>
      ))}
    </div>
  ) : (
    <SyncTypeChip>ALWAYS</SyncTypeChip>
  );

// A selectable row in the steps list. Clicking it loads the step into the inspector.
const StepRow = ({
  step,
  resolved,
  selected,
  onSelect,
  expandable,
  expanded,
  onToggleExpand,
}: {
  step: DraftStep;
  resolved: ResolvedStep;
  selected: boolean;
  onSelect: () => void;
  expandable?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
}) => (
  <div
    onClick={onSelect}
    className={`relative flex cursor-pointer items-center gap-3 border-b border-gray-10 px-4 py-2.5 last:border-b-0 ${
      selected ? "" : "hover:bg-[#F9FAFB]"
    } ${step.enabled ? "" : "opacity-50"}`}
  >
    {selected && (
      <span className="absolute inset-y-0 left-0 w-0.5" style={{ backgroundColor: ACCENT }} />
    )}
    <KindBadge isNestedPlan={resolved.isNestedPlan} />
    <div className="min-w-0 flex-1">
      {resolved.targetName && (
        <Text variant="md" className="font-medium">
          {resolved.targetName}
        </Text>
      )}
      <Text variant="sm" className="block font-mono text-gray-60">
        {resolved.targetId}
      </Text>
    </div>
    <div className="w-44">
      <SyncTypeCell syncTypes={resolved.syncTypes} />
    </div>
    <EnabledBadge enabled={step.enabled} />
    {expandable && (
      <button
        onClick={(event) => {
          event.stopPropagation();
          onToggleExpand?.();
        }}
        aria-label={expanded ? "Collapse nested plan" : "Expand nested plan"}
        className="rounded p-1 text-gray-60 hover:bg-gray-10 hover:text-gray-90"
      >
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
    )}
  </div>
);

// Right-hand inspector panel showing the selected step's full detail.
const StepInspector = ({
  step,
  resolved,
  onOpenPlan,
  onOpenBlueprint,
  onEdit,
}: {
  step: PlanStep;
  resolved: ResolvedStep;
  onOpenPlan: (id: string) => void;
  onOpenBlueprint: (versionId: string) => void;
  onEdit: () => void;
}) => {
  const ruleGroups = summarizeEligibility(step.eligibility);
  const metadataKeys = Object.keys(step.metadata || {});
  // Narrow the conditional ids to non-null locals so the JSX needs no casts.
  const nestedPlanId = resolved.isNestedPlan ? step.nested_plan_id : null;
  const blueprintVersionId = resolved.isNestedPlan ? null : resolved.blueprintVersionId;

  return (
    <div className="sticky top-0 flex flex-col gap-4 self-start p-4">
      <div className="flex justify-end">
        <Button
          size="sm"
          variant={ButtonVariant.SecondaryGray}
          leftIcon={<Pencil size={13} />}
          onClick={onEdit}
        >
          Edit
        </Button>
      </div>

      <div className="flex flex-col gap-3">
        <div>
          <Text variant="sm" className="mb-1 block uppercase tracking-wide text-gray-50">
            {resolved.isNestedPlan ? "Nested plan" : "Blueprint"}
          </Text>
          <Text variant="sm" className="break-all font-mono text-gray-80">
            {resolved.targetId || "(none selected)"}
          </Text>
        </div>

        <div className="flex gap-8">
          <div>
            <Text variant="sm" className="mb-1 block uppercase tracking-wide text-gray-50">
              Priority
            </Text>
            <Text variant="md" className="font-mono">
              {step.priority}
            </Text>
          </div>
          <div>
            <Text variant="sm" className="mb-1 block uppercase tracking-wide text-gray-50">
              Status
            </Text>
            <EnabledBadge enabled={step.enabled} />
          </div>
        </div>

        <div>
          <Text variant="sm" className="mb-1 block uppercase tracking-wide text-gray-50">
            Eligibility
          </Text>
          {ruleGroups.length === 0 ? (
            <div className="flex items-center justify-between gap-3 rounded-md border border-gray-10 bg-white px-2.5 py-1.5">
              <Text variant="sm" className="text-gray-50">
                sync type
              </Text>
              <Text variant="sm" className="font-mono text-gray-80">
                ALWAYS
              </Text>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {ruleGroups.map((parts, ruleIndex) => (
                <div key={ruleIndex} className="flex flex-col gap-1">
                  {ruleIndex > 0 && (
                    <Text variant="sm" className="font-mono uppercase text-gray-40">
                      or
                    </Text>
                  )}
                  {parts.map((part, partIndex) => (
                    <div
                      key={partIndex}
                      className="flex items-center justify-between gap-3 rounded-md border border-gray-10 bg-white px-2.5 py-1.5"
                    >
                      <Text variant="sm" className="text-gray-50">
                        {part.label}
                      </Text>
                      <Text variant="sm" className="font-mono text-gray-80">
                        {part.value}
                      </Text>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {metadataKeys.length > 0 && (
          <div>
            <Text variant="sm" className="mb-1 block uppercase tracking-wide text-gray-50">
              Metadata
            </Text>
            <pre className="overflow-auto rounded-md bg-[#F5F5F4] p-2 font-mono text-xs text-gray-80">
              {JSON.stringify(step.metadata, null, 2)}
            </pre>
          </div>
        )}

        {nestedPlanId && (
          <Button
            size="sm"
            variant={ButtonVariant.SecondaryGray}
            rightIcon={<ExternalLink size={14} />}
            onClick={() => onOpenPlan(nestedPlanId)}
          >
            Open nested plan
          </Button>
        )}

        {blueprintVersionId && (
          <Button
            size="sm"
            variant={ButtonVariant.SecondaryGray}
            rightIcon={<ExternalLink size={14} />}
            onClick={() => onOpenBlueprint(blueprintVersionId)}
          >
            Open blueprint
          </Button>
        )}
      </div>
    </div>
  );
};

// Read-only inline view of a nested plan's steps, fetched on expand so a nested
// plan can be inspected without navigating away. Nested-plan steps within it
// expand recursively (depth-capped to avoid runaway on self-referential plans).
const NestedPlanSteps = ({
  planId,
  blueprintsById,
  planNamesById,
  depth,
}: {
  planId: string;
  blueprintsById: BlueprintsById;
  planNamesById: Record<string, string>;
  depth: number;
}) => {
  const { data: plan, isLoading } = useConductorResource<Plan>(`/conductor/plans/${planId}`);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const indent = { paddingLeft: 12 + depth * 20 };

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 border-b border-gray-10 bg-[#FBFBFA] py-1.5"
        style={indent}
      >
        <Text variant="sm" className="text-gray-60">
          Loading nested plan…
        </Text>
      </div>
    );
  }
  if (!plan) {
    return (
      <div
        className="flex items-center gap-2 border-b border-gray-10 bg-[#FBFBFA] py-1.5"
        style={indent}
      >
        <Text variant="sm" className="text-gray-60">
          Nested plan not found.
        </Text>
      </div>
    );
  }

  const steps = [...plan.steps].sort((a, b) => a.priority - b.priority);
  const canRecurse = depth < 5;
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="bg-[#FBFBFA]">
      <div className="flex items-center gap-2 border-b border-gray-10 py-1.5" style={indent}>
        <Text variant="sm" className="font-semibold text-gray-70">
          {plan.name}
        </Text>
        <ModeBadge mode={plan.mode || fullPlanMode("RUN_ALL")} />
        {!plan.enabled && <Badge color="gray">Disabled</Badge>}
      </div>
      {steps.length === 0 ? (
        <div className="border-b border-gray-10 py-2" style={indent}>
          <Text variant="sm" className="text-gray-60">
            No steps.
          </Text>
        </div>
      ) : (
        steps.map((step, index) => {
          const resolved = resolveStep(step, blueprintsById, planNamesById);
          const key = step.id ?? `nested-${index}`;
          const stepExpandable = resolved.isNestedPlan && !!step.nested_plan_id && canRecurse;
          const isExpanded = expanded.has(key);
          return (
            <div key={key}>
              <div
                className={`flex items-center gap-3 border-b border-gray-10 py-2 pr-4 ${
                  step.enabled ? "" : "opacity-50"
                }`}
                style={indent}
              >
                <KindBadge isNestedPlan={resolved.isNestedPlan} />
                <div className="min-w-0 flex-1">
                  {resolved.targetName && (
                    <Text variant="sm" className="font-medium">
                      {resolved.targetName}
                    </Text>
                  )}
                  <Text variant="sm" className="block font-mono text-gray-60">
                    {resolved.targetId}
                  </Text>
                </div>
                <div className="w-44">
                  <SyncTypeCell syncTypes={resolved.syncTypes} />
                </div>
                <EnabledBadge enabled={step.enabled} />
                {stepExpandable && (
                  <button
                    onClick={() => toggle(key)}
                    aria-label={isExpanded ? "Collapse nested plan" : "Expand nested plan"}
                    className="rounded p-1 text-gray-60 hover:bg-gray-10 hover:text-gray-90"
                  >
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                )}
              </div>
              {stepExpandable && isExpanded && step.nested_plan_id && (
                <NestedPlanSteps
                  planId={step.nested_plan_id}
                  blueprintsById={blueprintsById}
                  planNamesById={planNamesById}
                  depth={depth + 1}
                />
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

const ConductorPlanDetail = ({ planId, onOpenPlan, onDeleted }: Props) => {
  const history = useHistory();
  const [reloadToken, setReloadToken] = useState(0);
  const [planSummariesReloadToken, setPlanSummariesReloadToken] = useState(0);
  const { data: plan, isLoading } = useConductorResource<Plan>(
    `/conductor/plans/${planId}`,
    "Error fetching plan",
    reloadToken
  );
  const [isBlueprintsLoading, setIsBlueprintsLoading] = useState(true);
  const [blueprintsById, setBlueprintsById] = useState<BlueprintsById>({});
  const [blueprintOptions, setBlueprintOptions] = useState<BlueprintOption[]>([]);
  const [draft, setDraft] = useState<PlanDraft | null>(null);
  const [selectedLocalId, setSelectedLocalId] = useState<string | null>(null);
  const [expandedStepIds, setExpandedStepIds] = useState<Set<string>>(() => new Set());
  const [editing, setEditing] = useState<{ localId: string | null } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const nextLocalId = useRef(0);

  // Seed the editable draft from the loaded plan (and re-seed after a save, when
  // the plan refetches). Reset transient UI state on plan change.
  useEffect(() => {
    if (plan) setDraft(draftFromPlan(plan));
    setSelectedLocalId(null);
    setEditing(null);
  }, [plan]);

  // Resolve blueprint ids to names (+ version ids for nav) once the integration
  // is known. Best-effort: on failure we fall back to the raw blueprint id.
  const integrationId = plan?.integration_id;
  useEffect(() => {
    if (!integrationId) return;
    let active = true;
    setIsBlueprintsLoading(true);
    fetchWithAuth({
      path: `/integrations/${integrationId}/blueprints`,
      method: "GET",
      onResponse: (data: { blueprints?: BlueprintMeta[] } | BlueprintMeta[]) => {
        if (!active) return;
        const list = Array.isArray(data) ? data : data.blueprints || [];
        const map: BlueprintsById = {};
        const options: BlueprintOption[] = [];
        const seen = new Set<string>();
        for (const bp of list) {
          const humanName = bp.human_name && bp.human_name.trim() ? bp.human_name : bp.name;
          const ref: BlueprintRef = { name: humanName, versionId: bp.id };
          if (bp.id) map[bp.id] = ref;
          if (bp.blueprint_id) map[bp.blueprint_id] = ref;
          const optionId = bp.blueprint_id || bp.id;
          if (optionId && !seen.has(optionId)) {
            seen.add(optionId);
            options.push({ id: optionId, name: humanName });
          }
        }
        setBlueprintsById(map);
        setBlueprintOptions(options);
        setIsBlueprintsLoading(false);
      },
      onError: () => {
        if (!active) return;
        setIsBlueprintsLoading(false);
      },
    });
    return () => {
      active = false;
    };
  }, [integrationId]);

  // Sibling plans for the nested-plan picker in the step editor.
  const { data: planSummaries } = useConductorResource<PlanSummary[]>(
    integrationId ? `/conductor/plan-summaries?integration_id=${integrationId}` : null,
    undefined,
    planSummariesReloadToken
  );
  const planOptions = useMemo<BlueprintOption[]>(
    () =>
      (planSummaries || [])
        .filter((p) => p.id !== planId)
        .filter((p) => !plan?.category || p.category === plan.category)
        .map((p) => ({ id: p.id, name: p.name })),
    [planSummaries, planId, plan?.category]
  );
  const planNamesById = useMemo<Record<string, string>>(
    () => Object.fromEntries((planSummaries || []).map((p) => [p.id, p.name])),
    [planSummaries]
  );

  // Steps grouped into priority tiers (sorted by priority, then id), each
  // resolved (name/sync types/nav target) once.
  const tiers = useMemo(() => {
    if (!draft) return [];
    const sorted = [...draft.steps].sort(
      (a, b) => a.priority - b.priority || a.localId.localeCompare(b.localId)
    );
    const byPriority = new Map<number, { step: DraftStep; resolved: ResolvedStep }[]>();
    for (const step of sorted) {
      const group = byPriority.get(step.priority) || [];
      group.push({ step, resolved: resolveStep(step, blueprintsById, planNamesById) });
      byPriority.set(step.priority, group);
    }
    return [...byPriority.entries()].map(([priority, rows]) => ({ priority, rows }));
  }, [draft, blueprintsById, planNamesById]);

  const allRows = useMemo(() => tiers.flatMap((tier) => tier.rows), [tiers]);
  const selectedRow =
    allRows.find((row) => row.step.localId === selectedLocalId) || allRows[0] || null;

  const onOpenBlueprint = (versionId: string) => {
    if (integrationId) navigateToBlueprintEditor(history, integrationId, versionId, true);
  };

  if (isLoading || (plan && isBlueprintsLoading)) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (!plan) {
    return (
      <Text variant="md" className="block py-16 text-center text-gray-60">
        Plan not found.
      </Text>
    );
  }

  if (!draft) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const { settings } = draft;
  const activeMode = displayPlanMode(settings.mode);
  const isRunOne = activeMode === "RUN_ONE";
  const suggestedPriority = draft.steps.reduce((max, step) => Math.max(max, step.priority), -1) + 1;

  const updateSettings = (patch: Partial<PlanSettingsDraft>) =>
    setDraft((prev) => (prev ? { ...prev, settings: { ...prev.settings, ...patch } } : prev));

  const applyStep = (applied: PlanStep) => {
    setDraft((prev) => {
      if (!prev) return prev;
      if (editing?.localId != null) {
        const localId = editing.localId;
        return {
          ...prev,
          steps: prev.steps.map((s) => (s.localId === localId ? { ...applied, localId } : s)),
        };
      }
      return {
        ...prev,
        steps: [...prev.steps, { ...applied, localId: `new-${nextLocalId.current++}` }],
      };
    });
    setSelectedLocalId(editing?.localId ?? null);
    setEditing(null);
  };

  const removeStep = () => {
    const localId = editing?.localId;
    if (localId == null) return;
    setDraft((prev) =>
      prev ? { ...prev, steps: prev.steps.filter((s) => s.localId !== localId) } : prev
    );
    setEditing(null);
  };

  const discard = () => setDraft(draftFromPlan(plan));

  const settingsDirty = JSON.stringify(settings) !== JSON.stringify(settingsFromPlan(plan));
  const stepsDirty =
    JSON.stringify(draft.steps.map(canonicalStep)) !==
    JSON.stringify(plan.steps.map(canonicalStep));
  const dirty = settingsDirty || stepsDirty;

  // Conductor's CreateOrUpdatePlan replaces the plan's steps with the embedded
  // list, so we persist settings + the full step set in one atomic upsert.
  // Omitting a step deletes it; a step without an id is created.
  const saveAll = async () => {
    setIsSaving(true);
    const { ok } = await conductorPost("/conductor/plans", {
      id: plan.id,
      integration_id: plan.integration_id,
      category: plan.category,
      name: plan.name,
      mode: settings.mode,
      max_concurrent_steps: settings.maxConcurrentSteps,
      enforce_strict_priority_ordering: settings.strictPriority,
      require_sequential_success: settings.sequentialSuccess,
      enabled: settings.enabled,
      steps: draft.steps.map((step) => ({
        ...(step.id ? { id: step.id } : {}),
        type: step.type,
        blueprint_id: step.blueprint_id,
        nested_plan_id: step.nested_plan_id,
        eligibility: step.eligibility,
        priority: step.priority,
        enabled: step.enabled,
        metadata: step.metadata,
      })),
    });
    setIsSaving(false);
    if (ok) {
      showSuccessToast("Changes saved");
      setReloadToken((token) => token + 1);
    } else {
      showErrorToast("Save failed");
    }
  };

  const deletePlan = async () => {
    setIsDeleting(true);
    const { ok, status } = await conductorDelete(`/conductor/plans/${planId}`);
    setIsDeleting(false);
    if (ok) {
      showSuccessToast("Plan deleted");
      onDeleted();
    } else if (status === 409) {
      showErrorToast(
        "This plan is still referenced by a playbook entry or another plan. Remove those first."
      );
    } else {
      showErrorToast("Failed to delete plan");
    }
  };

  const editingStep =
    editing && editing.localId != null
      ? draft.steps.find((s) => s.localId === editing.localId) ?? null
      : null;

  return (
    <div className="pb-8">
      {/* Page header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Text variant="h4">{plan.name}</Text>
          <div className="mt-1 flex items-center gap-2">
            <Text variant="sm" className="font-mono text-gray-60">
              {plan.id}
            </Text>
            {plan.category && (
              <>
                <span className="text-gray-40">·</span>
                <Text variant="sm" className="font-mono text-gray-60">
                  {plan.category}
                </Text>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Text variant="md" className="font-semibold text-gray-90">
              {settings.enabled ? "Enabled" : "Disabled"}
            </Text>
            <Toggle
              checked={settings.enabled}
              onChange={(value) => updateSettings({ enabled: value })}
            />
          </div>
          <Button
            size="sm"
            variant={ButtonVariant.DangerText}
            leftIcon={<Trash2 size={14} />}
            onClick={() => setConfirmDelete(true)}
          >
            Delete plan
          </Button>
        </div>
      </div>

      {/* Mode bar (cached in the draft; persisted on Save) */}
      <div className="mb-4 grid grid-cols-[auto_1fr_1fr] gap-2 rounded-lg border border-gray-10 bg-white p-2">
        <div className="flex items-center px-3">
          <Text variant="sm" className="font-mono uppercase tracking-wide text-gray-60">
            Mode
          </Text>
        </div>
        {MODES.map((modeOption) => {
          const isActive = activeMode === modeOption.value;
          return (
            <button
              key={modeOption.value}
              onClick={() => updateSettings({ mode: fullPlanMode(modeOption.value) })}
              className={`rounded-md border px-3 py-2 text-left ${
                isActive ? "" : "border-transparent hover:bg-gray-0"
              }`}
              style={
                isActive
                  ? { borderColor: ACCENT_BORDER_TINT, backgroundColor: ACCENT_BG_TINT }
                  : undefined
              }
            >
              <div className="flex items-center gap-2">
                <Text variant="md" className="font-mono font-semibold">
                  {modeOption.value}
                </Text>
                {isActive && <Check size={13} color={ACCENT} />}
              </div>
              <Text variant="md" className="text-gray-60">
                {modeOption.description}
              </Text>
            </button>
          );
        })}
      </div>

      {/* Concurrency & priority gates */}
      <ConductorCard title="Concurrency & priority" padBody>
        <div className="flex flex-col">
          {isRunOne ? (
            <Text variant="md" className="py-2 text-gray-60">
              RUN_ONE dispatches exactly one step, so concurrency caps and priority gates don&apos;t
              apply.
            </Text>
          ) : (
            <>
              <GateRow
                label="Max concurrent steps"
                description="The most steps that can run at the same time."
              >
                <div className="w-24">
                  <TextField
                    className="bg-white"
                    type="number"
                    value={String(settings.maxConcurrentSteps ?? "")}
                    onChange={(e) => updateSettings({ maxConcurrentSteps: Number(e.target.value) })}
                  />
                </div>
              </GateRow>
              <GateRow
                label="Strict priority ordering"
                description="Finish each priority tier before starting the next."
              >
                <Toggle
                  checked={settings.strictPriority}
                  onChange={(value) => updateSettings({ strictPriority: value })}
                />
              </GateRow>
              <GateRow
                label="Require sequential success"
                description="Skip a tier if any lower-priority step failed."
              >
                <Toggle
                  checked={settings.sequentialSuccess}
                  onChange={(value) => updateSettings({ sequentialSuccess: value })}
                />
              </GateRow>
            </>
          )}
        </div>
      </ConductorCard>

      {/* Steps: tier list on the left, inspector on the right */}
      <ConductorCard
        title="Plan steps"
        subtitle={`${draft.steps.length}`}
        actions={
          <div className="flex items-center gap-2">
            {isRunOne && <Badge color="purple">router: first match wins</Badge>}
            <Button
              size="sm"
              variant={ButtonVariant.SecondaryGray}
              leftIcon={<Plus size={14} />}
              onClick={() => setEditing({ localId: null })}
            >
              Add step
            </Button>
          </div>
        }
      >
        {allRows.length === 0 ? (
          <Text variant="md" className="block py-10 text-center text-gray-60">
            This plan has no steps yet.
          </Text>
        ) : (
          <div className="grid grid-cols-[1fr_400px]">
            <div className="border-r border-gray-10">
              {tiers.map((tier) => (
                <div key={tier.priority}>
                  <div className="flex items-center gap-2 border-b border-gray-10 bg-[#F5F5F4] px-4 py-2">
                    <Text
                      variant="sm"
                      className="font-semibold uppercase tracking-wide text-gray-60"
                    >
                      Priority {tier.priority}
                    </Text>
                    {!isRunOne && tier.rows.length > 1 && (
                      <Badge color="blue">{tier.rows.length} parallel</Badge>
                    )}
                    {isRunOne && (
                      <Badge color="purple">router: one of {tier.rows.length} runs</Badge>
                    )}
                  </div>
                  {tier.rows.map(({ step, resolved }) => {
                    const expandable = resolved.isNestedPlan && !!step.nested_plan_id;
                    const isExpanded = expandedStepIds.has(step.localId);
                    return (
                      <div key={step.localId}>
                        <StepRow
                          step={step}
                          resolved={resolved}
                          selected={selectedRow?.step.localId === step.localId}
                          onSelect={() => setSelectedLocalId(step.localId)}
                          expandable={expandable}
                          expanded={isExpanded}
                          onToggleExpand={() =>
                            setExpandedStepIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(step.localId)) next.delete(step.localId);
                              else next.add(step.localId);
                              return next;
                            })
                          }
                        />
                        {expandable && isExpanded && step.nested_plan_id && (
                          <NestedPlanSteps
                            planId={step.nested_plan_id}
                            blueprintsById={blueprintsById}
                            planNamesById={planNamesById}
                            depth={1}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            {selectedRow && (
              <StepInspector
                step={selectedRow.step}
                resolved={selectedRow.resolved}
                onOpenPlan={onOpenPlan}
                onOpenBlueprint={onOpenBlueprint}
                onEdit={() => setEditing({ localId: selectedRow.step.localId })}
              />
            )}
          </div>
        )}
      </ConductorCard>

      {/* Bottom save bar: all edits are cached until saved here */}
      {dirty && (
        <div className="mt-6 flex items-center gap-3 border-t border-gray-10 pt-4">
          <Text variant="sm" className="mr-auto text-gray-60">
            You have unsaved changes.
          </Text>
          <Button variant={ButtonVariant.SecondaryGray} onClick={discard} disabled={isSaving}>
            Discard
          </Button>
          <Button
            variant={ButtonVariant.PrimaryGray}
            leftIcon={<Check size={14} />}
            onClick={saveAll}
            disabled={isSaving}
          >
            Save changes
          </Button>
        </div>
      )}

      {editing && (
        <StepEditorDrawer
          key={editing.localId ?? "new"}
          step={editingStep}
          suggestedPriority={suggestedPriority}
          integrationId={plan.integration_id}
          category={plan.category}
          blueprintOptions={blueprintOptions}
          planOptions={planOptions}
          onClose={() => setEditing(null)}
          onApply={applyStep}
          onRemove={editing.localId != null ? removeStep : undefined}
          onPlanCreated={() => setPlanSummariesReloadToken((token) => token + 1)}
        />
      )}

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        variant="sm"
        title="Delete plan?"
        primaryButtonText="Delete plan"
        primaryButtonVariant={ButtonVariant.DangerFilled}
        primaryButtonLoading={isDeleting}
        onPrimaryButtonClick={deletePlan}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setConfirmDelete(false)}
      >
        <Text variant="md" className="text-gray-70">
          This permanently deletes <span className="font-semibold">{plan.name}</span> and its steps.
          A plan that is still attached to a playbook entry or used as a nested plan can&apos;t be
          deleted.
        </Text>
      </Dialog>
    </div>
  );
};

export default ConductorPlanDetail;
